import {Plain} from '../../../../../../shared/model/plain.type.model';
import {LabelTranslator} from '../../../model/label-translator.model';
import {EmailContent} from './email-content.model';
import {MediaStatus} from './media-status.model';

/**
 * Le média e-mail d'une séquence : l'en-tête du message — qui l'envoie, sous quel objet — et son
 * corps, composé dans l'éditeur.
 *
 * « Média e-mail » et non « e-mail » : ce n'est pas un message envoyé, mais le modèle à partir
 * duquel la séquence en enverra autant qu'elle a de destinataires.
 *
 * Chaque média existe en deux versions, distinguées par `prodVersion` : le brouillon que l'éditeur
 * retouche, et celle qui est servie aux destinataires jusqu'à la publication suivante.
 */
export class MediaEmail {
    public id: number;
    public sequenceId: number;
    public senderName: string;
    public senderEmail: string;
    public object: string;
    public content: EmailContent;
    public published: boolean;
    /** `null` tant que le média n'a jamais été publié. */
    public lastPublicationDate: string | null;
    public status: MediaStatus;
    public prodVersion: boolean;
    public locale: string;

    // `content` est relâché par rapport à `Plain` : l'API l'omet sur un média qui vient d'être
    // créé, et `EmailContent` sait en faire un corps vide.
    constructor(data: Omit<Plain<MediaEmail>, 'content'> & {content?: Plain<EmailContent> | null}) {
        this.id = data.id;
        this.sequenceId = data.sequenceId;
        // L'API omet les champs non renseignés : on normalise en chaînes vides, jamais en null —
        // ce sont des champs de saisie, et un `null` y afficherait le mot.
        this.senderName = data.senderName ?? '';
        this.senderEmail = data.senderEmail ?? '';
        this.object = data.object ?? '';
        this.content = new EmailContent(data.content);
        this.published = data.published ?? false;
        this.lastPublicationDate = data.lastPublicationDate ?? null;
        this.status = data.status;
        this.prodVersion = data.prodVersion ?? false;
        this.locale = data.locale;
    }

    /**
     * Le média se supprime-t-il encore ?
     *
     * Seul un média jamais mis en service : dès qu'il est prêt ou actif, la séquence s'en sert et
     * le retirer emporterait des envois. C'est la règle du serveur, reprise ici pour que l'écran
     * puisse éteindre le bouton sans attendre le refus.
     */
    public readonly isDeletable = (): boolean => this.status === 'IN_PROGRESS';

    /** Ce qui précède l'arobase dans l'adresse d'expédition : la part que l'utilisateur saisit. */
    public readonly senderLocalPart = (): string => this.senderEmail.split('@')[0] ?? '';

    /** Ce qui suit l'arobase : un domaine choisi dans la liste que le serveur autorise. */
    public readonly senderDomain = (): string => this.senderEmail.split('@')[1] ?? '';

    public readonly withSenderName = (senderName: string): MediaEmail => new MediaEmail({...this, senderName});

    /**
     * L'adresse se saisit en deux morceaux mais ne circule qu'entière : le back ne connaît que
     * `senderEmail`.
     */
    public readonly withSenderAddress = (localPart: string, domain: string): MediaEmail =>
        new MediaEmail({...this, senderEmail: `${localPart}@${domain}`});

    public readonly withObject = (object: string): MediaEmail => new MediaEmail({...this, object});

    public readonly withContent = (content: EmailContent): MediaEmail => new MediaEmail({...this, content});

    /**
     * Le média, les libellés que le gabarit du serveur a laissés en clés remplacés par leur texte.
     *
     * Le serveur crée l'e-mail à partir d'un gabarit dont le corps, le libellé du bouton et le lien
     * de désabonnement sont des clés : elles deviennent du texte à l'ouverture, une fois pour
     * toutes. Sans cela le brouillon les lui renverrait telles quelles, et l'e-mail partirait au
     * destinataire en clés.
     *
     * Rendu à l'identique quand il n'y avait rien à traduire : rouvrir un e-mail déjà corrigé
     * n'enregistre alors rien.
     */
    public readonly withTranslatedTexts = (translate: LabelTranslator): MediaEmail => {
        const content = this.content.withTranslatedTexts(translate);

        return content === this.content ? this : new MediaEmail({...this, content});
    };
}
